import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import BASE_URL from "../config";
import "../pages/Home/Home.css";
import { useCart } from "../context/CartContext";

import ProductCard from "../components/RudrakshaSection/ProductCard";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import RudrakshaVideos from "../components/RudrakshaSection/RudrakshaVideos";
import Loader from "../components/Loader/Loader";
import { useAuth } from "../context/AuthContext";
import DOMPurify from "dompurify";
import { FiHeadphones, FiShare2 } from "react-icons/fi";
import { FiTruck, FiPackage, FiCreditCard } from "react-icons/fi";

import {
  FaGem,
  FaAward,
  FaSeedling,
  FaBoxOpen,
} from "react-icons/fa";


import ProductFaq from "../components/ProductFaq/ProductFaq";
import ProductOptions from "../components/ProductOptions/ProductOptions";
import {
  defaultSelection,
  describeSelection,
  missingRequired,
  normalizeGroups,
  optionsLabel as buildOptionsLabel,
  optionsPrice,
  selectedChoiceIds as pickChoiceIds,
} from "../utils/productOptions";
import {
  buildCheckoutSummary,
  lineSubtotal,
  payableAfterDiscount,
} from "../utils/orderPricing";
import { getProductById, getRelatedProducts } from "../api/productApi";
import {
  getProductReviews,
  getMyReview,
  createReview,
  updateReview,
} from "../api/reviewApi";
import {
  getCouponsForProduct,
  validateCoupon,
  getLoginOffer,
} from "../api/couponApi";

/* =========================================================
   API BASE URL
========================================================= */

// const API_BASE_URL = "YOUR_BACKEND_BASE_URL";

/* =========================================================
   IMAGE HELPER
========================================================= */

// const getImageUrl = (image) => {
//   if (!image) {
//     return "/placeholder-product.jpg";
//   }

//   if (
//     image.startsWith("http://") ||
//     image.startsWith("https://") ||
//     image.startsWith("data:")
//   ) {
//     return image;
//   }

//   if (image.startsWith("/")) {
//     return `${API_BASE_URL}${image}`;
//   }

//   return `${API_BASE_URL}/${image}`;
// };

const IMAGE_BASE_URL = BASE_URL.replace(/\/api\/v1\/?$/, "");

const getImageUrl = (image) => {
  if (!image) return "/placeholder-product.jpg";

  if (/^(https?:\/\/|data:|blob:)/i.test(image)) {
    return image;
  }

  return `${IMAGE_BASE_URL}/${image.replace(/^\/+/, "")}`;
};
const features = [
  {
    icon: <FaGem />,
    title: "Guarantee of Purity",
  },
  {
    icon: <FaAward />,
    title: "100% Certified",
  },
  {
    icon: <FaSeedling />,
    title: "Ethically Sourced",
  },
  {
    icon: <FaBoxOpen />,
    title: "Aura Synchronized",
  },
  {
    icon: <FaBoxOpen />,
    title: "Free Shipping",
  },
];

export default function ProductDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, accessToken } = useAuth();

  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mainImage, setMainImage] = useState("");
  const [quantity, setQuantity] = useState(1);

  // The option groups the admin set up for this product, what the buyer has chosen from them
  // ({ groupId: [choiceId] }), and the ids that go to the coupon check and the order.
  const optionGroups = useMemo(
    () => normalizeGroups(product?.optionGroups),
    [product],
  );
  const [selection, setSelection] = useState({});
  const [showOptionErrors, setShowOptionErrors] = useState(false);
  const chosenChoiceIds = useMemo(
    () => pickChoiceIds(optionGroups, selection),
    [optionGroups, selection],
  );
  const selectionKey = chosenChoiceIds.join(",");
  useEffect(() => {
    setSelection(defaultSelection(optionGroups));
    setShowOptionErrors(false);
  }, [optionGroups]);

  const [reviews, setReviews] = useState([]);
  const [reviewSummary, setReviewSummary] = useState({
    averageRating: 0,
    totalReviews: 0,
  });
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [myReview, setMyReview] = useState(null);
  const [editingReview, setEditingReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewFormError, setReviewFormError] = useState("");
  const [reviewFormSuccess, setReviewFormSuccess] = useState("");

  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [couponCodeInput, setCouponCodeInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [applyingCoupon, setApplyingCoupon] = useState(false);

  const [subcategoryProducts, setSubcategoryProducts] = useState([]);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError("");
        setProduct(null);

        const result = await getProductById(id);

        console.log("Single Product API:", result);

        if (!result?.success) {
          setError(result?.message || "Product not found");
          return;
        }

        /*
          API response can be:

          {
            success: true,
            data: {
              product: {...}
            }
          }

          OR

          {
            success: true,
            data: {...}
          }
        */

        const productData = result?.data?.product || result?.data;

        if (!productData) {
          setError("Product not found");
          return;
        }

        setProduct(productData);

        const apiImages = Array.isArray(productData.images)
          ? productData.images
          : [];

        const primaryImage =
          apiImages.find(
            (img) => img?.isPrimary === true || img?.isPrimary === 1,
          )?.imageUrl ||
          apiImages?.[0]?.imageUrl ||
          productData?.image ||
          productData?.imageUrl ||
          "";

        setMainImage(primaryImage);
      } catch (err) {
        console.error("Product API Error:", err);
        setError("Unable to load product");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);

  const fetchReviews = async (productId) => {
    setLoadingReviews(true);
    try {
      const result = await getProductReviews(productId);
      if (result?.success) {
        setReviews(result.data?.reviews || []);
        setReviewSummary(
          result.data?.summary || { averageRating: 0, totalReviews: 0 },
        );
      }
    } catch (err) {
      console.error("Reviews API Error:", err);
    } finally {
      setLoadingReviews(false);
    }
  };

  useEffect(() => {
    if (product?.id) {
      fetchReviews(product.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  useEffect(() => {
    const fetchMyReview = async () => {
      if (!product?.id || !isLoggedIn) {
        setMyReview(null);
        setEditingReview(false);
        return;
      }

      try {
        const result = await getMyReview(product.id, accessToken);
        if (result?.success && result.data) {
          setMyReview(result.data);
          setReviewForm({
            rating: result.data.rating,
            comment: result.data.comment,
          });
        } else {
          setMyReview(null);
        }
      } catch (err) {
        console.error("My Review API Error:", err);
      }
    };

    fetchMyReview();
  }, [product?.id, isLoggedIn, accessToken]);

  const handleReviewFormChange = (field, value) => {
    setReviewForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setReviewFormError("");
    setReviewFormSuccess("");

    if (!isLoggedIn) {
      navigate("/login", { state: { redirectTo: `/product/${id}` } });
      return;
    }

    if (!reviewForm.comment.trim()) {
      setReviewFormError("Please write your feedback before submitting.");
      return;
    }

    setSubmittingReview(true);

    try {
      const result = myReview
        ? await updateReview(myReview.id, reviewForm, accessToken)
        : await createReview(product.id, reviewForm, accessToken);

      if (!result?.success) {
        throw new Error(result?.message || "Failed to submit your review.");
      }

      setMyReview(result.data);
      setReviewFormSuccess(
        myReview
          ? "Your review has been updated."
          : "Thank you! Your review has been submitted.",
      );
      setEditingReview(false);
      fetchReviews(product.id);
    } catch (err) {
      setReviewFormError(
        err.message || "Something went wrong while submitting your review.",
      );
    } finally {
      setSubmittingReview(false);
    }
  };

  const renderStars = (value) => {
    const rounded = Math.round(Number(value) || 0);
    return "★".repeat(rounded) + "☆".repeat(Math.max(0, 5 - rounded));
  };

  useEffect(() => {
    const fetchCoupons = async () => {
      if (!product?.id) return;
      setLoadingCoupons(true);
      try {
        const result = await getCouponsForProduct(product.id);
        if (result?.success) {
          setAvailableCoupons(result.data || []);
        }
      } catch (err) {
        console.error("Coupons API Error:", err);
      } finally {
        setLoadingCoupons(false);
      }
    };

    fetchCoupons();
  }, [product?.id]);

  useEffect(() => {
    const fetchRelated = async () => {
      if (!product?.id) return;
      setLoadingRelated(true);
      try {
        const result = await getRelatedProducts(product.id, 8);
        if (result?.success) {
          setSubcategoryProducts(result.data?.subcategoryProducts || []);
          setCategoryProducts(result.data?.categoryProducts || []);
        }
      } catch (err) {
        console.error("Related Products API Error:", err);
      } finally {
        setLoadingRelated(false);
      }
    };

    fetchRelated();
  }, [product?.id]);

  // A coupon's discount is computed for the quantity and options in effect at the
  // moment it was applied — if the buyer changes either afterward, drop it
  // rather than show a stale discount and require re-applying.
  useEffect(() => {
    setAppliedCoupon(null);
    setCouponError("");
  }, [quantity, selectionKey]);

  const handleApplyCoupon = async (codeOverride, { silent = false } = {}) => {
    const code = (codeOverride || couponCodeInput).trim();
    setCouponError("");

    if (!code) {
      setCouponError("Please enter a coupon code.");
      return;
    }

    setApplyingCoupon(true);

    try {
      const result = await validateCoupon(
        code,
        [
          {
            productId: product.id,
            quantity,
            selectedChoiceIds: chosenChoiceIds,
          },
        ],
        accessToken,
      );

      if (!result?.success) {
        throw new Error(result?.message || "This coupon could not be applied.");
      }

      setAppliedCoupon(result.data);
      setCouponCodeInput(result.data.code);
    } catch (err) {
      setAppliedCoupon(null);
      // The automatic login discount can fail quietly (e.g. below its minimum
      // order) - only show errors for codes the buyer typed themselves.
      if (!silent)
        setCouponError(err.message || "This coupon could not be applied.");
    } finally {
      setApplyingCoupon(false);
    }
  };

  // Logged-in (OTP-verified) customers get the login reward applied for them.
  // Only one coupon can be active: this runs only when none is applied, and a
  // code the buyer applies afterwards simply replaces it. Removing it is
  // respected - it is not re-applied for the same product and quantity.
  const autoOfferKey = useRef("");
  useEffect(() => {
    if (!isLoggedIn || !product?.id || appliedCoupon) return undefined;
    const key = `${product.id}:${quantity}:${selectionKey}`;
    if (autoOfferKey.current === key) return undefined;
    autoOfferKey.current = key;

    let cancelled = false;
    getLoginOffer().then((result) => {
      if (!cancelled && result?.success && result.data?.code) {
        handleApplyCoupon(result.data.code, { silent: true });
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, product?.id, quantity, selectionKey, appliedCoupon]);

  const handleAddToCart = () => {
    const missing = missingRequired(optionGroups, selection);

    if (missing.length > 0) {
      setShowOptionErrors(true);

      setTimeout(() => {
        document.querySelector(".product-options .po-error")?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 50);

      return;
    }

    addToCart({
      productId: product.id,
      title: productTitle,
      image: getImageUrl(mainImage),
      quantity,
      unitPrice: totalPrice,
      selectedChoiceIds: chosenChoiceIds,
      optionsLabel: selectionText,
    });
  };
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponCodeInput("");
    setCouponError("");
  };

  const formatCouponDiscount = (coupon) =>
    coupon.discountType === "percentage"
      ? `${Number(coupon.discountValue)}% OFF`
      : `₹${Number(coupon.discountValue).toLocaleString("en-IN")} OFF`;

  if (loading) {
    return (
      <>
        <Header />

        <div
          style={{
            minHeight: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader label="Loading product..." />
        </div>

        <Footer />
      </>
    );
  }

  if (error || !product) {
    return (
      <>
        <Header />

        <div
          style={{
            minHeight: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            padding: "80px 20px",
          }}
        >
          <h2>{error || "Product Not Found"}</h2>

          <Link to="/" style={{ marginTop: "20px" }}>
            Go Back Home
          </Link>
        </div>

        <Footer />
      </>
    );
  }

  const productTitle = product.title || product.name || "Product";

  const productCategory = product.category?.name || product.category || "";

  const productSubCategory =
    product.subCategory?.name || product.subCategory || "";

  const productDescription = product.description || "";

  const productPrice =
    Number(
      String(product.price ?? product.sellingPrice ?? 0).replace(/[^\d.]/g, ""),
    ) || 0;

  const productOldPrice =
    product.oldPrice || product.mrp || product.comparePrice || "";

  const productDiscount = product.discount || "";

  const productRating = reviewSummary.totalReviews
    ? Number(reviewSummary.averageRating).toFixed(1)
    : product.reviewSummary?.totalReviews
      ? Number(product.reviewSummary.averageRating).toFixed(1)
      : product.rating || "New";

  const productReviews =
    reviewSummary.totalReviews || product.reviewSummary?.totalReviews || 0;

  const productSold = product.sold || "215";

  const apiImages = Array.isArray(product.images) ? product.images : [];

  const productImages = [
    ...apiImages
      .map((img) => img?.imageUrl || img?.url || img?.path)
      .filter(Boolean),

    product.image,
    product.image2,
    product.image3,
    product.imageUrl,
    product.imageUrl2,
    product.imageUrl3,
  ].filter(Boolean);

  const uniqueImages = [...new Set(productImages)];

  const categoryText = `${productCategory} ${productSubCategory} ${
    product.name || ""
  } ${product.title || ""}`.toLowerCase();

  const isRudrakshaProduct = categoryText.includes("rudraksha");

  const isGemstoneProduct =
    categoryText.includes("gemstone") || categoryText.includes("gemstone");

  let mainCategory = "shop";

  if (isRudrakshaProduct) {
    mainCategory = "rudrakshas";
  } else if (isGemstoneProduct) {
    mainCategory = "gemstones";
  } else if (categoryText.includes("crystal")) {
    mainCategory = "crystals";
  } else if (
    categoryText.includes("conch") ||
    categoryText.includes("shankh") ||
    categoryText.includes("shaligram")
  ) {
    mainCategory = "conch-shells-shaligrams";
  } else if (categoryText.includes("vastu")) {
    mainCategory = "vastu-products";
  } else if (categoryText.includes("tulsi")) {
    mainCategory = "tulsi-product";
  } else if (categoryText.includes("pooja")) {
    mainCategory = "pooja-essentials";
  } else if (categoryText.includes("astrology")) {
    mainCategory = "shop-by-astrology";
  } else if (categoryText.includes("numerology")) {
    mainCategory = "shop-by-birth-number";
  } else if (categoryText.includes("concern")) {
    mainCategory = "shop-by-concern";
  } else if (categoryText.includes("perfume")) {
    mainCategory = "perfumes";
  }

  // /* =======================================================
  //    RESET WHEN PRODUCT CHANGES
  // ======================================================= */

  // useEffect(() => {
  //   setQuantity(1);
  //   setSelectedStyle("loose");
  //   setSelectedMetal("");
  //   setSelectedDesign("");
  //   setSelectedLab([]);
  //   setSelectedRingSize("");
  // }, [id]);

  /* =======================================================
     PRICE CALCULATION
  ======================================================= */

  const basePrice = productPrice;

  // The options the buyer chose add to the price of ONE piece; the server prices
  // exactly the same choices, so what is shown here is what is charged.
  const selectedLines = describeSelection(optionGroups, selection);
  const selectionText = buildOptionsLabel(optionGroups, selection);
  const totalPrice =
    Math.round((basePrice + optionsPrice(optionGroups, selection)) * 100) / 100;

  const couponDiscount = appliedCoupon
    ? Number(appliedCoupon.discountAmount) || 0
    : 0;
  // `totalPrice` above is the price of ONE piece (base + chosen extras); the
  // buyer pays that times the quantity, less any coupon.
  const lineTotal = lineSubtotal(totalPrice, quantity);
  const payableTotal = payableAfterDiscount(lineTotal, couponDiscount);

  // Address collection and payment (PayU / COD) both happen on the
  // checkout page now — this just hands off the selected item + the
  // already-computed price summary. Guests are allowed through too; the
  // checkout page collects guest contact details itself.
  const handleBuyNow = () => {
    // Required options must be chosen first (the server insists too).
    if (missingRequired(optionGroups, selection).length > 0) {
      setShowOptionErrors(true);
      setTimeout(
        () =>
          document
            .querySelector(".product-options .po-error")
            ?.scrollIntoView({ behavior: "smooth", block: "center" }),
        50,
      );
      return;
    }

    navigate("/checkout", {
      state: {
        items: [
          {
            productId: product.id,
            quantity,
            selectedChoiceIds: chosenChoiceIds,
          },
        ],
        summary: buildCheckoutSummary({
          title: productTitle,
          image: getImageUrl(mainImage),
          quantity,
          optionsLabel: selectionText,
          unitPrice: totalPrice,
          discount: couponDiscount,
          couponCode: appliedCoupon ? appliedCoupon.code : undefined,
        }),
      },
    });
  };

  const categorySlug = productCategory
    ?.toString()
    .toLowerCase()
    .replace(/\s+/g, "-");

  const subCategorySlug = productSubCategory
    ?.toString()
    .toLowerCase()
    .replace(/\s+/g, "-");

  return (
    <>
      <Header />
      <div className="breadcrumb premium-breadcrumb">
        <Link to="/">Home</Link>

        <span> › </span>

        <Link to={`/shop/${mainCategory}`}>{productCategory}</Link>

        {productSubCategory && (
          <>
            <span> › </span>

            <Link to={`/shop/${mainCategory}/${subCategorySlug}`}>
              {productSubCategory}
            </Link>
          </>
        )}

        <span> › </span>

        <span>{productTitle}</span>
      </div>

      <section className="product-details premium-product-page">
        <div className="containerb">
          <div className="details-grid premium-details-grid">
            <div className="image-box premium-image-box">
              <div className="premium-image-layout">
                {/* THUMBNAILS */}

                <div className="thumbs premium-thumbs">
                  {uniqueImages.map((img, index) => (
                    <button
                      type="button"
                      key={index}
                      className={`premium-thumb ${
                        mainImage === img ? "active" : ""
                      }`}
                      onClick={() => setMainImage(img)}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`${productTitle} ${index + 1}`}
                      />
                    </button>
                  ))}
                </div>

                {/* MAIN IMAGE */}

                <div className="premium-main-image-wrapper">
                  <img
                    className="main-image premium-main-image"
                    src={getImageUrl(mainImage)}
                    alt={productTitle}
                  />
                </div>
              </div>

              {/* IMAGE FEATURES */}

              <div className="premium-image-features">
                <div>
                  <span>✦</span>
                  <p>100% Original</p>
                </div>

                <div>
                  <span>♜</span>
                  <p>Lab Certified</p>
                </div>

                <div>
                  <span>✥</span>
                  <p>Premium Quality</p>
                </div>

                <div>
                  <span>♙</span>
                  <p>Secure Packaging</p>
                </div>
              </div>
            </div>

            {/* =================================================
                PRODUCT INFO
            ================================================= */}

            <div className="info-box premium-info-box">
              {/* TITLE */}

              <div className="premium-title-row">
                <div>
                  <h1>{productTitle}</h1>

                  <div className="premium-rating">
                    <span className="stars">
                      {renderStars(
                        reviewSummary.totalReviews
                          ? reviewSummary.averageRating
                          : product.reviewSummary?.averageRating,
                      )}
                    </span>

                    <span>{productRating}</span>

                    <span>({productReviews} Reviews)</span>

                    <span className="rating-divider">|</span>

                    <span>{productSold} Sold</span>
                  </div>
                </div>

                <button
                  type="button"
                  className="premium-share-btn"
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({
                        title: productTitle,
                        url: window.location.href,
                      });
                    }
                  }}
                >
                  <FiShare2 />
                  Share
                </button>
              </div>

              {/* PRICE */}

              <div className="premium-price-row">
                <h2>₹{totalPrice.toLocaleString("en-IN")}</h2>

                {productOldPrice && <del>{productOldPrice}</del>}

                {productDiscount && (
                  <span className="premium-discount">
                    {productDiscount} OFF
                  </span>
                )}
              </div>

              {/* =================================================
                  PRODUCT OPTIONS
                  Everything the admin set up for this product (style, metal, design,
                  size, certificates ...). The server prices exactly what is chosen here.
              ================================================= */}

              <ProductOptions
                groups={optionGroups}
                selection={selection}
                onChange={setSelection}
                getImageUrl={getImageUrl}
                showErrors={showOptionErrors}
              />

              {/* =================================================
                  PRICE SUMMARY
              ================================================= */}

              <div className="premium-price-summary">
                <div>
                  <span>Product Price</span>

                  <strong>₹{basePrice.toLocaleString("en-IN")}</strong>
                </div>

                {/* CHOSEN OPTIONS that add to the price */}

                {selectedLines
                  .filter((line) => line.priceDelta > 0)
                  .map((line) => (
                    <div key={`${line.groupName}-${line.label}`}>
                      <span>{line.label}</span>

                      <strong>
                        + ₹{line.priceDelta.toLocaleString("en-IN")}
                      </strong>
                    </div>
                  ))}

                {selectionText && (
                  <small className="selection-summary">
                    Your selection: {selectionText}
                  </small>
                )}

                {/* QUANTITY: the rows above are for ONE piece */}

                {quantity > 1 && (
                  <div>
                    <span>Quantity</span>

                    <strong>× {quantity}</strong>
                  </div>
                )}

                {/* COUPON DISCOUNT */}

                {appliedCoupon && (
                  <div>
                    <span>Coupon ({appliedCoupon.code})</span>

                    <strong style={{ color: "#2e7d32" }}>
                      − ₹{couponDiscount.toLocaleString("en-IN")}
                    </strong>
                  </div>
                )}

                {/* TOTAL */}

                <div className="total-price-row">
                  <span>Total Price</span>

                  <strong>₹{payableTotal.toLocaleString("en-IN")}</strong>
                </div>

                <small>Inclusive of all taxes</small>
              </div>

              {/* =================================================
                  COUPON
              ================================================= */}

              <div className="coupon-apply-box">
                {appliedCoupon ? (
                  <div className="coupon-applied-row">
                    <span>
                      ✓ Coupon <strong>{appliedCoupon.code}</strong> applied —
                      you saved ₹{couponDiscount.toLocaleString("en-IN")}
                    </span>

                    <button type="button" onClick={handleRemoveCoupon}>
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="coupon-input-row">
                    <input
                      type="text"
                      placeholder="Enter coupon code"
                      value={couponCodeInput}
                      onChange={(e) =>
                        setCouponCodeInput(e.target.value.toUpperCase())
                      }
                    />

                    <button
                      type="button"
                      onClick={() => handleApplyCoupon()}
                      disabled={applyingCoupon}
                    >
                      {applyingCoupon ? "Applying..." : "Apply"}
                    </button>
                  </div>
                )}

                {couponError && <p className="coupon-error">{couponError}</p>}

                {availableCoupons.length > 0 && !appliedCoupon && (
                  <div className="coupon-suggestions">
                    {availableCoupons.map((coupon) => (
                      <button
                        type="button"
                        key={coupon.id}
                        className="coupon-chip"
                        onClick={() => handleApplyCoupon(coupon.code)}
                      >
                        {coupon.code} — {formatCouponDiscount(coupon)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* =================================================
                  PURCHASE
              ================================================= */}

              <div className="premium-purchase-row">
                <div className="premium-quantity">
                  <button
                    type="button"
                    onClick={() =>
                      setQuantity((prev) => (prev > 1 ? prev - 1 : 1))
                    }
                  >
                    −
                  </button>

                  <span>{quantity}</span>

                  <button
                    type="button"
                    onClick={() => setQuantity((prev) => prev + 1)}
                  >
                    +
                  </button>
                </div>

                <button
                  type="button"
                  className="premium-cart-btn"
                  onClick={handleAddToCart}
                >
                  ADD TO CART
                </button>
                <button
                  type="button"
                  className="premium-buy-btn"
                  onClick={handleBuyNow}
                >
                  BUY NOW
                </button>
              </div>

              <div className="premium-secure-payment">
                Secure UPI&nbsp; • &nbsp;Cards&nbsp; • &nbsp;COD
              </div>
            </div>
          </div>
        </div>

        <section className="premium-trust-bar">
          {features.map((feature, index) => (
            <React.Fragment key={index}>
              <div className="premium-trust-item">
                <div className="premium-trust-icon">{feature.icon}</div>

                <div>
                  <strong>{feature.title}</strong>

                  <span>Premium quality assurance</span>
                </div>
              </div>

              {index !== features.length - 1 && (
                <div className="premium-trust-divider" />
              )}
            </React.Fragment>
          ))}
        </section>

        {!loadingCoupons && availableCoupons.length > 0 && (
          <section className="coupons-section">
            <div className="container">
              <h2 className="related-heading">Available Coupons</h2>

              <div className="coupons-grid">
                {availableCoupons.map((coupon) => (
                  <div className="coupon-card" key={coupon.id}>
                    <div className="coupon-card-code">{coupon.code}</div>

                    <div className="coupon-card-body">
                      <strong>{formatCouponDiscount(coupon)}</strong>

                      {coupon.description && <p>{coupon.description}</p>}

                      {Number(coupon.minOrderAmount) > 0 && (
                        <span>
                          Min. order ₹
                          {Number(coupon.minOrderAmount).toLocaleString(
                            "en-IN",
                          )}
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      className="coupon-card-apply"
                      onClick={() => handleApplyCoupon(coupon.code)}
                      disabled={appliedCoupon?.code === coupon.code}
                    >
                      {appliedCoupon?.code === coupon.code
                        ? "Applied"
                        : "Apply"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="product-info-section">
          <h2 className="product-info-heading">Product Information</h2>

          {/* <details open className="info-accordion">
            <summary>Description</summary>

            <div className="info-content">
              <p>{productDescription}</p>
            </div>
          </details> */}
<details open className="info-accordion">
  <summary>Description</summary>

 <div className="info-content">
  <div
    className="product-description"
    dangerouslySetInnerHTML={{
      __html: DOMPurify.sanitize(productDescription || ""),
    }}
  />
</div>
</details>
         
          <details className="info-accordion">
            <summary>Usage Guidelines</summary>

            <div className="info-content">
              <p>
                {product.howToWear ||
                  product.usage ||
                  "Please follow the recommended usage guidelines for this product."}
              </p>

              <div className="product-extra">
                <p>
                  <strong>Origin :</strong> {product.origin || "Authentic"}
                </p>

                <p>
                  <strong>Mukhi :</strong> {product.mukhi || "N/A"}
                </p>

                <p>
                  <strong>Consultation :</strong>{" "}
                  {product.consultation || "Available"}
                </p>
              </div>
            </div>
          </details>

          <details className="info-accordion">
            <summary>Quality & Certifications</summary>

            <div className="info-content">
              <p>
                {product.certification ||
                  "Quality checked and certified product."}
              </p>
            </div>
          </details>
        </section>

        <section className="reviews-section">
          <div className="container">
            <h2 className="related-heading">Customer Reviews</h2>

            <div className="reviews-summary-row">
              <span className="stars">
                {renderStars(reviewSummary.averageRating)}
              </span>

              <strong>
                {Number(reviewSummary.averageRating || 0).toFixed(1)}
              </strong>

              <span>
                ({reviewSummary.totalReviews}{" "}
                {reviewSummary.totalReviews === 1 ? "Review" : "Reviews"})
              </span>
            </div>

            {loadingReviews ? (
              <p>Loading reviews...</p>
            ) : reviews.length === 0 ? (
              <p>No reviews yet. Be the first to share your experience!</p>
            ) : (
              <div className="reviews-list">
                {reviews.map((review) => (
                  <div className="review-card" key={review.id}>
                    <div className="review-card-header">
                      <span className="stars">
                        {renderStars(review.rating)}
                      </span>

                      <strong>{review.user?.name || "Anonymous"}</strong>
                    </div>

                    <p>{review.comment}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="review-form-box">
              <h3>
                {myReview && !editingReview
                  ? "Your Review"
                  : myReview
                    ? "Edit Your Review"
                    : "Write a Review"}
              </h3>

              {myReview && !editingReview ? (
                <div className="review-card">
                  <span className="stars">{renderStars(myReview.rating)}</span>

                  <p>{myReview.comment}</p>

                  <button
                    type="button"
                    className="learn-btn"
                    onClick={() => setEditingReview(true)}
                  >
                    Edit Review
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmitReview} className="review-form">
                  <div className="premium-select-wrapper">
                    <select
                      value={reviewForm.rating}
                      onChange={(e) =>
                        handleReviewFormChange("rating", Number(e.target.value))
                      }
                      className="custom-select premium-select"
                    >
                      {[5, 4, 3, 2, 1].map((star) => (
                        <option key={star} value={star}>
                          {star} Star{star === 1 ? "" : "s"}
                        </option>
                      ))}
                    </select>
                  </div>

                  <textarea
                    rows={4}
                    placeholder="Share your experience with this product..."
                    value={reviewForm.comment}
                    onChange={(e) =>
                      handleReviewFormChange("comment", e.target.value)
                    }
                  />

                  {reviewFormError && (
                    <p style={{ color: "#c0392b" }}>{reviewFormError}</p>
                  )}

                  {reviewFormSuccess && (
                    <p style={{ color: "#2e7d32" }}>{reviewFormSuccess}</p>
                  )}

                  <button
                    type="submit"
                    className="premium-buy-btn"
                    disabled={submittingReview}
                  >
                    {submittingReview
                      ? "Submitting..."
                      : !isLoggedIn
                        ? "Login to Review"
                        : myReview
                          ? "Update Review"
                          : "Submit Review"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        <ProductFaq faqs={product.faqs} productName={product.name} />

        {!loadingRelated && subcategoryProducts.length > 0 && (
          <section className="related-products-section">
            <div className="container">
              <h2 className="related-heading">
                More from {productSubCategory || "this Subcategory"}
                 <div className="about-heading-line">
            <img
              src="https://res.cloudinary.com/dbb2hkjri/image/upload/v1786441287/1684481897-flower-divider-horizontal-orange_f2omyx.svg"
              alt="Heading decoration"
            />
          </div>
              </h2>

              <div className="related-grid">
                {subcategoryProducts.map((item) => (
                  <ProductCard key={item.id} product={item} />
                ))}
              </div>
            </div>
          </section>
        )}

        {!loadingRelated && categoryProducts.length > 0 && (
          <section className="related-products-section">
            <div className="container">
              <h2 className="related-heading">You May Also Like</h2>

              <div className="related-grid">
                {categoryProducts.map((item) => (
                  <ProductCard key={item.id} product={item} />
                ))}
              </div>
            </div>
          </section>
        )}

     
        <section className="info-section">
          <div className="containeraa">
            <h2 className="info-heading">
              Things you want to know
             
              before purchase
               <div className="about-heading-line">
            <img
              src="https://res.cloudinary.com/dbb2hkjri/image/upload/v1786441287/1684481897-flower-divider-horizontal-orange_f2omyx.svg"
              alt="Heading decoration"
            />
          </div>
            </h2>

            <div className="info-wrapper">
              {/* SHIPPING */}

              <div className="info-card">
                <div className="icon-boxx">
                  <FiTruck />
                </div>

                <h3>Shipping Policy</h3>

                <ul>
                  <li>
                    Enjoy <strong>free shipping</strong> across India on all
                    orders.
                  </li>

                  <li>Cash On Delivery available on eligible orders.</li>
                </ul>

                <Link to="/Shipping-Policy" className="learn-btn">
                  View Shipping Policy
                </Link>
              </div>

              {/* RETURN */}

              <div className="info-card">
                <div className="icon-boxx">
                  <FiPackage />
                </div>

                <h3>Return Policy</h3>

                <ul>
                  <li>100% money-back guarantee within 7 days.</li>

                  <li>Taxes and shipping charges are non-refundable.</li>
                </ul>

                <Link to="/refund" className="learn-btn">
                  View Return Policy
                </Link>
              </div>

              {/* PAYMENT */}

              <div className="info-card">
                <div className="icon-boxx">
                  <FiCreditCard />
                </div>

                <h3>Payment Methods</h3>

                <ul>
                  <li>
                    Credit Cards: We accept Visa, MasterCard, and American
                    Express.
                  </li>

                  <li>Debit Cards: All major Indian cards are accepted.</li>

                  <li>Other options: Pay via Net Banking.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </section>
      <RudrakshaVideos />
      <section className="support-section">
        <div className="support-content">
          <h2>Need Support? Get Consultation Now!</h2>

          <p>Need to chat with us? We are happy to assist you.</p>
          <Link to="/contact" className="support-btn">
            <FiHeadphones />
            Get Support
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
